import './markup-panel.less';
import classNames from 'classnames';
import React from 'react';
import { Title } from '@meitu/saas-ui';
import { IMarkupLayerProps, MarkupType } from '@meitu/saas-editor-core/es/version-types';
import { BaseToolbar } from '@meitu/saas-editor-ui/es/components/base-toolbar';
import { DividerLine } from '@meitu/saas-editor-ui/es/components/divider-line';
import { HeaderToolbar } from '@meitu/saas-editor-ui/es/components/header-toolbar';
import { usePanelLayerId } from '@/hooks';
import { useLocaleStore } from '@/i18n';
import { editor } from '@/instance';
import { FillPointFeature } from './components/fill-point/fill-point';
import { FillFeature } from './components/fill/fill';
import { LabelBackground } from './components/label/background';
import { LabelFontFamily } from './components/label/font-family';
import { LabelFontSize } from './components/label/font-size';
import { LabelPosition } from './components/label/position';
import { LabelRotate } from './components/label/rotate';
import { LabelStyle } from './components/label/style';
import { LabelText } from './components/label/text';
import { LineFeature } from './components/line/line';
import { OpacityFeature } from './components/opacity/opacity';
import { StyleFeature } from './components/style/style';
import { WidthFeature } from './components/width/width';

const { SimpleTitle } = Title;

const MarkupTitle: React.FC<{
    title: string;
    paddingTop?: number;
    border?: boolean;
    fontSize?: number;
}> = React.memo(({ title, border = false, paddingTop = 0, fontSize }) => {
    return (
        <div
            className={classNames('c-markup-panel__title', {
                'c-markup-panel__title-border': border,
            })}
            style={{ paddingTop }}
        >
            <SimpleTitle text={title} fontSize={fontSize} />
        </div>
    );
});

const MarkupPanel = () => {
    const layerId = usePanelLayerId();
    const { $t } = useLocaleStore();

    if (!layerId) return null;

    const layer = editor.stage.getLayer(layerId);
    const { markupType } = layer.data as IMarkupLayerProps;

    return (
        <div className="c-markup-panel">
            <HeaderToolbar id={layerId} editor={editor} style={{ padding: 16 }} />

            <MarkupTitle
                title={$t((v) => v.panel.markupPanel[markupType === MarkupType.Segment ? 'segment' : 'label'])}
                fontSize={14}
            />
            {/* 类型,数值,单位,文案 */}
            <LabelText
                id={layerId}
                trackCode={{
                    type: '109',
                    text: '109',
                    num: '109',
                    units: '109',
                }}
            />
            {/* 位置 */}
            <LabelPosition id={layerId} trackCode="109" />
            {/* 角度 */}
            <LabelRotate id={layerId} trackCode="109" />
            {/* 字体 */}
            <LabelFontFamily id={layerId} trackCode="109" />
            {/* 字号,颜色 */}
            <LabelFontSize
                id={layerId}
                trackCode={{
                    size: '109',
                    color: '109',
                }}
            />
            {/* 样式 */}
            <LabelStyle
                id={layerId}
                trackCode={{
                    bold: '109',
                    italic: '109',
                }}
            />
            {/* 背景色 */}
            <LabelBackground id={layerId} trackCode="109" />
            <DividerLine />
            <MarkupTitle title={$t((v) => v.panel.markupPanel.lineStyle)} paddingTop={0} fontSize={14} />
            {/* 颜色 */}
            <FillFeature id={layerId} trackCode="19" />
            {/* 样式 */}
            <StyleFeature id={layerId} trackCode="19" />
            {/* 粗细 */}
            <WidthFeature id={layerId} maxValue={100} minValue={1} trackCode="19" />
            {/* 端点 */}
            <FillPointFeature
                id={layerId}
                trackCode={{
                    point: '19',
                    swap: '19',
                }}
            />
            {/* 显示延长线 */}
            <LineFeature id={layerId} trackCode="19" />
            <DividerLine />
            {/* 图形透明度 */}
            <OpacityFeature id={layerId} trackCode="21" />

            <DividerLine />

            {/* 通用功能 */}
            <BaseToolbar id={layerId} editor={editor} style={{ padding: '0 16px 16px' }} />
        </div>
    );
};

export default React.memo(MarkupPanel);
